import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";

const DOMAIN_REGEX = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export const normalizeDomain = (value: string) => value
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, "")
  .replace(/^www\./, "")
  .replace(/\/.*$/, "")
  .replace(/^@/, "");

export type DomainListInputProps = {
  label: string;
  description?: string;
  placeholder?: string;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
};

const DomainListInput = ({
  label,
  description,
  placeholder,
  values,
  onChange,
  disabled,
}: DomainListInputProps) => {
  const [draft, setDraft] = useState("");

  // Accepts a single domain or a separated list (typed, Enter/comma, or pasted).
  const addMany = (raw: string) => {
    const candidates = raw.split(/[\s,;\n]+/).map(normalizeDomain).filter(Boolean);
    if (!candidates.length) {
      setDraft("");
      return;
    }
    const next = [...values];
    const invalid: string[] = [];
    let added = 0;
    for (const domain of candidates) {
      if (!DOMAIN_REGEX.test(domain)) {
        invalid.push(domain);
        continue;
      }
      if (!next.includes(domain)) {
        next.push(domain);
        added += 1;
      }
    }
    if (added) onChange(next);
    if (invalid.length) toast.error(`Not a valid domain: ${invalid.join(", ")}`);
    setDraft("");
  };

  const removeDomain = (domain: string) => {
    onChange(values.filter((item) => item !== domain));
  };

  return (
    <div className="space-y-2">
      <div>
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        {description ? (
          <p className="mt-0.5 text-[12px] text-muted-foreground">{description}</p>
        ) : null}
      </div>

      {/* Input + explicit Add button */}
      <div className="flex items-center gap-2">
        <input
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              addMany(draft);
            } else if (event.key === "Backspace" && !draft && values.length > 0) {
              removeDomain(values[values.length - 1]);
            }
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData("text");
            if (/[\s,;\n]/.test(text)) {
              event.preventDefault();
              addMany((draft ? `${draft} ` : "") + text);
            }
          }}
          placeholder={placeholder || "example.com"}
          className={cn(
            "h-9 flex-1 rounded-md border border-input bg-background px-3 text-[13px] outline-none",
            "focus:ring-1 focus:ring-ring placeholder:text-muted-foreground disabled:opacity-50",
          )}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !draft.trim()}
          onClick={() => addMany(draft)}
          className="h-9 shrink-0"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Press Enter or comma to add. You can also paste a comma- or space-separated list.
      </p>

      {/* Added domains */}
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {values.map((domain) => (
            <span
              key={domain}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[12px] text-foreground"
            >
              {domain}
              <button
                type="button"
                onClick={() => removeDomain(domain)}
                disabled={disabled}
                className="text-muted-foreground transition-colors hover:text-destructive"
                aria-label={`Remove ${domain}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground/70">No domains added yet.</p>
      )}
    </div>
  );
};

export default DomainListInput;
